import { memo } from "react";
import { createSelector } from "reselect";
import { connect } from "react-redux";
import _get from "lodash/get";

import PurchaseOrder from "../models/PurchaseOrder";
import Revision from "../models/Revision";
import wrapPORevision from "./wrapPORevision";
import { getDataComponent } from "../reducers/dataComponentReducer";
import * as REQUEST_TYPES from "../constants/RequestTypes";
import { getResourcesV2 } from "../utils/bluechipUtils";
import { createDeepEqualSelector } from "../utils/selectors";

const findPurchaseOrder = (resources, purchaseOrderId) =>
  purchaseOrderId &&
  PurchaseOrder.query(resources)
    .where({ id: [purchaseOrderId] })
    .toObjects()[0];

const selectLegacyRevision = (resources, purchaseOrderId) => {
  const po = findPurchaseOrder(resources, purchaseOrderId);
  if (!po) return {};

  const revision = Revision.query(resources)
    .where({ id: po.currentRevisionId })
    .includes(["revisionActivities"])
    .toObjects()[0];

  return {
    status: po.revisionStatus,
    activities: _get(revision, "revisionActivities", []),
  };
};

const legacySelector = () => {
  const poSelector = createSelector(
    state => state.resources,
    (_, { purchaseOrderId }) => purchaseOrderId,
    selectLegacyRevision
  );

  // BW-7020 follow-up: poSelector is keyed on `state.resources`, which any API
  // response replaces, so it recomputes constantly and rebuilds `activities` as
  // a fresh array. Comparing the result deep-equal keeps the previous reference
  // whenever the revision data itself is unchanged — otherwise connect()
  // re-renders, wrapPORevision's useMemo is invalidated, and every
  // PORevisionContext consumer (plus every effect depending on `revisionValue`)
  // re-runs on each unrelated store update.
  return createDeepEqualSelector(poSelector, revision => revision);
};

const legacyMapStateToProps = () => {
  const poSelector = legacySelector();

  return (state, { purchaseOrderId }) => {
    const { status, activities } = poSelector(state, { purchaseOrderId });
    return {
      isRevisionActive: Boolean(status),
      revisionActivities: activities,
    };
  };
};

const v2MapStateToProps = (dataComponentId, poPath, v1AndV2) => () => {
  const defaultPO = {};
  const poSelector = createSelector(
    (_, { po }) => po,
    po => {
      const revision = po.currentRevision;
      return {
        status: po.revisionStatus,
        activities: _get(revision, "revisionActivities", []),
      };
    }
  );

  return (state, { purchaseOrderId }) => {
    const dataComponent = getDataComponent(dataComponentId, state);
    const resource = getResourcesV2(
      dataComponent,
      state,
      REQUEST_TYPES.FIND,
      defaultPO
    );

    const po = poPath ? _get(resource, poPath, defaultPO) : resource;

    let status, activities;

    // In case nothing is found in v2 and v1AndV2 = true find the po on V1
    if (v1AndV2 && po == defaultPO) {
      const legacyPOSelector = legacySelector();
      ({ status, activities } = legacyPOSelector(state, {
        purchaseOrderId,
      }));
    } else {
      ({ status, activities } = poSelector(null, { po }));
    }

    return {
      isRevisionActive: Boolean(status),
      revisionActivities: activities,
    };
  };
};

/**
 * A public higher-order component to use the PO revision state
 * If v1AndV2 its true that means the component can be used from v1 and v2 parent components.
 */
export default function(WrappedComponent, dataComponentId, poPath, v1AndV2) {
  const PORevisionWrapper = wrapPORevision(WrappedComponent);

  const mapStateToProps = dataComponentId
    ? v2MapStateToProps(dataComponentId, poPath, v1AndV2)
    : legacyMapStateToProps;
  return memo(connect(mapStateToProps)(PORevisionWrapper));
}
